import { defineComponent as _defineComponent } from 'vue'

import { onMounted, onUnmounted } from "vue"

/**
 * Listens to keyboard events, and fires an event with the scanned input,
 * if it appears to come from an external keyboard device scanner.
 *
 * @listens onkeydown
 *
 * @see minimumRequiredCharacters
 * @see readerThreshold
 *
 * @event read Emit valid input.
 *
 * @example
 * <sfx-scanner-keyboard :readerThreshold="100" :minimumRequiredCharacters="12" @read="readInput" />
 * "Where readInput is a function that will receive a string with valid keyboard input."
 *
 */

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxScannerKeyboard',
  props: {
  /**
   * Minimum required number of characters for a scan to be considered valid.
   *
   * @default 12
   */
  minimumRequiredCharacters: {
    type: Number,
    default: 12,
    validator: (value: number) => {
      return value >= 0
    },
  },

  /**
   * Threshold in milliseconds in which input is expected. After threshold passes, input is reset.
   *
   * @default 100
   */
  readerThreshold: {
    type: Number,
    default: 100,
    validator: (value: number) => {
      return value >= 0
    },
  },
},
  emits: ["read"],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit

//endregion

//region Variables

let currentScanText = ""
let timeout: ReturnType<typeof setTimeout> | undefined

//endregion

//region Lifecycle

// Attach a listener on component init
onMounted(() => {
  window.addEventListener("keydown", keyboardListenerHandler)
})

// Remove listeners when component is removed
onUnmounted(() => {
  window.removeEventListener("keydown", keyboardListenerHandler)
})

//endregion

//region Functions

/**
 * Handler for keyboard events.
 *
 * If the event key is a valid character, then it is appended to currentScanText and a reset timer
 * for currentScanText is started.
 *
 * If the event key is "Enter", then the currentScanText is emitted back with event name "read", the reset timer and
 * currentScanText are both cleared.
 *
 * On any other event key do nothing.
 */
const keyboardListenerHandler = (e: KeyboardEvent) => {
  // Allow only characters and digits
  if (e.key.length === 1) {
    startResetTimer()
    currentScanText += e.key
  } else if (e.key === "Enter") {
    if (currentScanText.length >= props.minimumRequiredCharacters) {
      emit("read", currentScanText)
      currentScanText = ""
      clearTimeout(timeout)
    } else {
      // Reset when reading a code that is too short
      currentScanText = ""
    }
  }
}

/**
 * Restart a timer based on the provided readerThreshold. Once timer end, currentScanText is set to empty string.
 */
function startResetTimer() {
  // Passing an invalid ID to clearTimeout() silently does nothing; no exception is thrown.
  clearTimeout(timeout)
  timeout = setTimeout(() => {
    currentScanText = ""
  }, props.readerThreshold)
}

//endregion

return (_ctx: any,_cache: any) => {
  return null
}
}

})